/**
 * Global HTTP_STORM_DETECTED latch (Wave D).
 * Surfaces subscribe and pause outbound / refetch amplification.
 * Wired via stormAwareFetch + fair-queue freeze.
 */

export const HTTP_STORM_DETECTED_HEADER = 'HTTP_STORM_DETECTED';

/** Fallback when Retry-After seconds is missing/invalid (plan: 60s). */
export const HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS = 60_000;

type StormLatchListener = () => void;

interface StormLatchState {
  latched: boolean;
  /** Absolute expiry (Date.now() ms). */
  expiresAtMs: number;
}

let state: StormLatchState = {
  latched: false,
  expiresAtMs: 0,
};

const listeners = new Set<StormLatchListener>();

function notifyListeners(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Subscribers must not break the latch bus.
    }
  }
}

function expireIfNeeded(now: number = Date.now()): void {
  if (state.latched && now >= state.expiresAtMs) {
    state = { latched: false, expiresAtMs: 0 };
  }
}

/**
 * Arm (or extend) the storm latch from a 429 + HTTP_STORM_DETECTED response.
 * @param retryAfterSeconds Retry-After in seconds; ≤0 / NaN → default 60s.
 */
export function onHttpStormDetected(retryAfterSeconds: number): void {
  const seconds =
    typeof retryAfterSeconds === 'number' && Number.isFinite(retryAfterSeconds)
      ? retryAfterSeconds
      : 0;
  const ms =
    seconds > 0
      ? Math.floor(seconds * 1000)
      : HTTP_STORM_LATCH_DEFAULT_RETRY_AFTER_MS;
  const until = Date.now() + ms;
  if (!state.latched || until > state.expiresAtMs) {
    state = { latched: true, expiresAtMs: until };
    notifyListeners();
  }
}

export function isHttpStormLatched(): boolean {
  expireIfNeeded();
  return state.latched;
}

/** Remaining pause TTL in ms (0 when not latched). */
export function getHttpStormRetryAfterMs(): number {
  expireIfNeeded();
  if (!state.latched) {
    return 0;
  }
  return Math.max(0, state.expiresAtMs - Date.now());
}

/** Subscribe to latch arm/extend/clear. Returns unsubscribe. */
export function subscribeHttpStormLatch(listener: StormLatchListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function clearHttpStormLatchForTests(): void {
  state = { latched: false, expiresAtMs: 0 };
  notifyListeners();
}

function readHeaderCaseInsensitive(
  headers: Headers | Record<string, string | ReadonlyArray<string> | undefined> | null | undefined,
  name: string,
): string | null {
  if (headers == null) {
    return null;
  }
  if (typeof (headers as Headers).get === 'function') {
    const value = (headers as Headers).get(name);
    return value == null || value === '' ? null : value;
  }
  const record = headers as Record<string, string | ReadonlyArray<string> | undefined>;
  const target = name.toLowerCase();
  for (const key of Object.keys(record)) {
    if (key.toLowerCase() === target) {
      const raw = record[key];
      if (raw == null) {
        return null;
      }
      const value = Array.isArray(raw) ? raw[0] : raw;
      if (value == null || value === '') {
        return null;
      }
      return value;
    }
  }
  return null;
}

/**
 * True when response headers include HTTP_STORM_DETECTED (case-insensitive name).
 * Presence of a non-empty value is enough (BE may emit `1` / `true`).
 */
export function responseHasHttpStormDetected(
  headers: Headers | Record<string, string | ReadonlyArray<string> | undefined> | null | undefined,
): boolean {
  return readHeaderCaseInsensitive(headers, HTTP_STORM_DETECTED_HEADER) !== null;
}
